import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getRoute } from 'Config';
import useNavigate from 'Hooks/useNavigate';

import { ThemeId } from 'Store/appSettings/types';
import { clearLoginError } from 'Store/mfa/actions';
import { openModal } from 'Store/modals/actions';
import { MODAL_LOGIN } from 'Store/modals/constants';
import { getIsUserLoggedIn } from 'Store/user/selectors';

type UseLoginModalOptions = {
  loginBodyIcon?: React.ReactElement;
  loginBodyTitle?: string;
  loginBodySubtitle?: string;
  showAppDownloadLinks?: boolean;
  themeIdOverride?: ThemeId;
};

const useLoginModal = ({
  showAppDownloadLinks = false,
  loginBodyIcon,
  loginBodyTitle,
  loginBodySubtitle,
  themeIdOverride,
}: UseLoginModalOptions = {}) => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(getIsUserLoggedIn);
  const navigate = useNavigate();

  const modalProps = useMemo(
    () => ({
      loginBodyIcon,
      loginBodyTitle,
      loginBodySubtitle,
      showAppDownloadLinks,
      themeIdOverride,
      handleCloseModal: clearLoginError(),
    }),
    [loginBodyIcon, loginBodySubtitle, loginBodyTitle, showAppDownloadLinks, themeIdOverride]
  );

  const openLoginModal = useCallback(() => {
    if (isLoggedIn) {
      navigate(getRoute('recs'));
      return;
    }
    dispatch(
      openModal(MODAL_LOGIN, {
        props: modalProps,
      })
    );
  }, [navigate, modalProps, dispatch, isLoggedIn]);

  const openCreateAccountModal = useCallback(() => {
    if (isLoggedIn) {
      navigate(getRoute('recs'));
      return;
    }
    dispatch(
      openModal(MODAL_LOGIN, {
        props: {
          ...modalProps,
          isCreateAccount: true,
        },
      })
    );
  }, [isLoggedIn, dispatch, modalProps, navigate]);

  return {
    openLoginModal,
    openCreateAccountModal,
  };
};

export default useLoginModal;
